import React, { useContext } from 'react';
import styled from 'styled-components';
import CollapsedPanel from '../components/CollapsedPanel';
import ColorPicker from '../components/ColorPicker';
import NumberInput from '../components/NumberInput';
import InputBlock from '../components/InputBlock';
import SelectInput from '../components/SelectInput';
import { EditorContext } from '../../../utils/contexts';
import { FONT_FAMILIES } from '../constants';

const SectionTitle = styled.div`
  font-size: 0.72em;
  font-weight: 700;
  color: #aaa;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin: 14px 0 4px;
`;

const fontOptions = Object.entries(FONT_FAMILIES).map(([value, label]) => ({
  value,
  label,
}));

const weightOptions = [
  { value: '400', label: 'Regular' },
  { value: '600', label: 'Semi-bold' },
  { value: '700', label: 'Bold' },
];

export default function FormStylingPanel() {
  const { settings, updateSettings } = useContext(EditorContext);

  const s = (key, fallback) => settings[key] ?? fallback;

  return (
    <CollapsedPanel open={false} title="Form Styling">

      {/* ── Label styling ─────────────────────────────── */}
      <SectionTitle>Question Labels</SectionTitle>

      <InputBlock label="Font">
        <SelectInput
          value={s('question_font_family', '')}
          items={[{ value: '', label: 'Inherit', tier: 0 }, ...fontOptions.map(o => ({ ...o, tier: 0 }))]}
          onChange={(v) => updateSettings({ question_font_family: v, text_field_font_family: v })}
        />
      </InputBlock>

      <InputBlock label="Size">
        <NumberInput
          value={s('question_text_size', 14)}
          min={10}
          max={36}
          step={1}
          size="small"
          onChange={(v) => updateSettings({ question_text_size: v })}
        />
      </InputBlock>

      <InputBlock label="Weight">
        <SelectInput
          value={String(s('question_font_weight', '600'))}
          items={weightOptions.map(o => ({ ...o, tier: 0 }))}
          onChange={(v) => updateSettings({ question_font_weight: v })}
        />
      </InputBlock>

      <InputBlock label="Color">
        <ColorPicker
          value={s('question_text_color', '222222')}
          onChange={(color) => updateSettings({ question_text_color: color })}
        />
      </InputBlock>

      {/* ── Input field styling ───────────────────────── */}
      <SectionTitle>Input Fields</SectionTitle>

      <InputBlock label="Font Size">
        <NumberInput
          value={s('text_field_font_size', 14)}
          min={10}
          max={24}
          step={1}
          size="small"
          onChange={(v) => updateSettings({ text_field_font_size: v })}
        />
      </InputBlock>

      <InputBlock label="Text Color">
        <ColorPicker
          value={s('text_field_text_color', '333333')}
          onChange={(color) => updateSettings({ text_field_text_color: color })}
        />
      </InputBlock>

      <InputBlock label="Background">
        <ColorPicker
          value={s('text_field_background_color', 'fafafa')}
          onChange={(color) => updateSettings({ text_field_background_color: color })}
        />
      </InputBlock>

      <InputBlock label="Bg Opacity">
        <NumberInput
          value={s('text_field_background_opacity', 100)}
          min={0}
          max={100}
          step={5}
          size="small"
          onChange={(v) => updateSettings({ text_field_background_opacity: v })}
        />
      </InputBlock>

      <InputBlock label="Border Color">
        <ColorPicker
          value={s('text_field_border_color', 'dddddd')}
          onChange={(color) => updateSettings({ text_field_border_color: color })}
        />
      </InputBlock>

      <InputBlock label="Border Width">
        <NumberInput
          value={s('text_field_border_width', 1)}
          min={0}
          max={10}
          step={1}
          size="small"
          onChange={(v) => updateSettings({ text_field_border_width: v })}
        />
      </InputBlock>

      <InputBlock label="Border Radius">
        <NumberInput
          value={s('text_field_border_radius', 4)}
          min={0}
          max={50}
          step={1}
          size="small"
          onChange={(v) => updateSettings({ text_field_border_radius: v })}
        />
      </InputBlock>

      {/* ── Form header ───────────────────────────────── */}
      <SectionTitle>Form Header</SectionTitle>

      <InputBlock label="Title Size">
        <NumberInput
          value={s('form_title_size', 22)}
          min={14}
          max={48}
          step={1}
          size="small"
          onChange={(v) => updateSettings({ form_title_size: v })}
        />
      </InputBlock>

      <InputBlock label="Title Color">
        <ColorPicker
          value={s('form_title_color', '222222')}
          onChange={(color) => updateSettings({ form_title_color: color })}
        />
      </InputBlock>

    </CollapsedPanel>
  );
}
